// AdminLeaves.js — validation des demandes de conges
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { CalendarCheck, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';

const TYPES = { annual: 'Congé annuel', sick: 'Maladie', maternity: 'Maternité', paternity: 'Paternité', unpaid: 'Sans solde', other: 'Autre' };
const BADGES = {
  pending:  'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};
const LABELS = { pending: 'En attente', approved: 'Approuvé', rejected: 'Refusé' };

export default function AdminLeaves() {
  const [leaves, setLeaves] = useState([]);
  const [filtre, setFiltre] = useState('pending');
  const [motifs, setMotifs] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => api.get('/leaves', { params: filtre ? { status: filtre } : {} })
    .then(r => setLeaves(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filtre]); // eslint-disable-line

  const decider = async (id, action) => {
    setBusy(id);
    try {
      await api.put(`/leaves/${id}/decision`, { action, rejectionReason: motifs[id] || null });
      toast.success(action === 'approve' ? 'Congé approuvé — jours posés dans les présences' : 'Demande refusée');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally { setBusy(null); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <CalendarCheck className="w-7 h-7 text-amber-600" />
        <h1 className="text-2xl font-bold text-gray-800">Congés — validation</h1>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {[['pending', 'En attente'], ['approved', 'Approuvés'], ['rejected', 'Refusés'], ['', 'Tous']].map(([v, l]) => (
          <button key={v} onClick={() => setFiltre(v)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${filtre === v ? 'bg-amber-600 text-white' : 'bg-white border border-gray-200 text-gray-600'}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {leaves.length === 0 ? (
          <p className="p-8 text-sm text-gray-400 text-center">Aucune demande{filtre === 'pending' ? ' en attente' : ''}.</p>
        ) : leaves.map(l => (
          <div key={l.id} className="px-5 py-4 border-b border-gray-50">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  {l.user ? `${l.user.firstName} ${l.user.lastName}` : '—'}
                  <span className="text-gray-400 font-normal"> · {l.user?.department || 'sans département'}</span>
                </p>
                <p className="text-sm text-gray-600 mt-0.5">{TYPES[l.type] || l.type} — du {l.startDate} au {l.endDate} ({l.days} j)</p>
                {l.reason && <p className="text-xs text-gray-500 mt-0.5">Motif : {l.reason}</p>}
                {l.status === 'rejected' && l.rejectionReason && (
                  <p className="text-xs text-red-500 mt-0.5">Refus : {l.rejectionReason}</p>
                )}
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${BADGES[l.status]}`}>{LABELS[l.status]}</span>
            </div>
            {l.status === 'pending' && (
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <button onClick={() => decider(l.id, 'approve')} disabled={busy === l.id}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg disabled:opacity-50">
                  <Check className="w-4 h-4" /> Approuver
                </button>
                <input value={motifs[l.id] || ''} onChange={e => setMotifs({ ...motifs, [l.id]: e.target.value })}
                  placeholder="Motif du refus (facultatif)…"
                  className="px-3 py-2 border border-gray-200 rounded-lg text-xs w-64 focus:ring-2 focus:ring-red-400 outline-none" />
                <button onClick={() => decider(l.id, 'reject')} disabled={busy === l.id}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg disabled:opacity-50">
                  <X className="w-4 h-4" /> Refuser
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
